/**
 * Story 4.1 — Gedeelde scoringmodule (AD-3, AD-6)
 *
 * Pure, side-effect-vrije functies voor de v2-kerntakenscore. Geen
 * DB-afhankelijkheid, geen Node-only of browser-only imports — dit
 * package wordt zowel door de backend (persistentie, autoritatief) als
 * de frontend (live weergave in de wizard) geïmporteerd.
 *
 * Niemand berekent overlap-maanden of gewichten zelf; niemand
 * herimplementeert de formule (AD-3).
 *
 * Afrondingsconventie: tussenwaarden (gewichten, punten per kerntaak)
 * rekenen op volle precisie; alleen eind-aggregaten worden afgerond op 1
 * decimaal via `roundTo1Decimal` — gebruik die helper overal waar een
 * eind-aggregaat gepersisteerd of getoond wordt.
 */
/**
 * Rekent het takenblok (max 50 punten) voor één tijdvak/pakket.
 *
 * Formule per kerntaak (FR-6): punten = 50 x (timePercentage/100) x (score/5).
 * `score === null` (nog niet gescoord) telt als 0 punten in de
 * tussenberekening — de aanroepende code onderscheidt dit apart van een
 * echte score van 0 (die in dit domein niet bestaat, scores zijn 1-5) om
 * "nog niet compleet" te signaleren.
 */
export function calculateCoreTaskBlock(assignments) {
    const breakdown = assignments.map((a) => {
        const earnedPoints = a.score === null ? 0 : 50 * (a.timePercentage / 100) * (a.score / 5);
        return {
            coreTaskId: a.coreTaskId,
            timePercentage: a.timePercentage,
            score: a.score,
            earnedPoints,
        };
    });
    const totalPoints = breakdown.reduce((sum, item) => sum + item.earnedPoints, 0);
    return { totalPoints, breakdown };
}
/** Interne helper: maand-index (jaar*12 + maand-1) voor eenvoudige integer-aftrek. */
function monthIndex(year, month) {
    return year * 12 + (month - 1);
}
/** Parseert een ISO YYYY-MM-DD string naar {year, month} zonder Date/timezone-afhankelijkheid. */
function parseIsoYearMonth(iso) {
    const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(iso);
    if (!match) {
        throw new Error(`Ongeldige ISO-datum: ${iso}`);
    }
    return { year: Number(match[1]), month: Number(match[2]) };
}
/**
 * Bepaalt welke tijdvakken in het beoordelingsvenster vallen en hun
 * twaalfden-gewicht (AD-6).
 *
 * Het venster wordt eerst genormaliseerd naar hele kalendermaanden
 * (start -> 1e van de startmaand, eind -> laatste dag van de eindmaand).
 * Omdat tijdvakken al op maandgrenzen liggen (AD-1/AD-2) is elke overlap
 * daarna een geheel aantal maanden.
 *
 * Gewicht per tijdvak = maanden overlap / Σ overlap-maanden van alle
 * GESCOORDE (d.w.z. overlappende) tijdvakken — normalisatie over gedekte
 * maanden, niet over de volle vensterlengte. Een gat of tijdvak buiten
 * bereik telt simpelweg niet mee in de noemer.
 *
 * Bij 0 gedekte maanden: leeg/nul-resultaat (`weights: []`,
 * `totalCoveredMonths: 0`) — deze functie gooit zelf geen error; de
 * aanroepende code (evaluationService) vertaalt dit naar `NO_PERIOD_COVERAGE`.
 */
export function resolvePeriodsAndWeights(evaluationWindow, periods) {
    const windowStart = parseIsoYearMonth(evaluationWindow.start);
    const windowEnd = parseIsoYearMonth(evaluationWindow.end);
    const windowStartIdx = monthIndex(windowStart.year, windowStart.month);
    const windowEndIdx = monthIndex(windowEnd.year, windowEnd.month);
    const overlaps = [];
    for (const period of periods) {
        const periodStart = parseIsoYearMonth(period.startDate);
        const periodStartIdx = monthIndex(periodStart.year, periodStart.month);
        const periodEndIdx = period.endDate
            ? monthIndex(parseIsoYearMonth(period.endDate).year, parseIsoYearMonth(period.endDate).month)
            : windowEndIdx; // open tijdvak: clip op het venster-einde
        const overlapStart = Math.max(periodStartIdx, windowStartIdx);
        const overlapEnd = Math.min(periodEndIdx, windowEndIdx);
        const months = overlapEnd - overlapStart + 1;
        if (months > 0) {
            overlaps.push({ periodId: period.id, months });
        }
    }
    const totalCoveredMonths = overlaps.reduce((sum, o) => sum + o.months, 0);
    if (totalCoveredMonths === 0) {
        return { weights: [], totalCoveredMonths: 0 };
    }
    const weights = overlaps.map((o) => ({
        periodId: o.periodId,
        months: o.months,
        weight: o.months / totalCoveredMonths,
    }));
    return { weights, totalCoveredMonths };
}
/**
 * Rondt een eind-aggregaat af op 1 decimaal (AD-3 afrondingsconventie).
 * Gebruik dit UITSLUITEND bij het teruggeven/persisteren van eind-aggregaten
 * (blok- en totaalscores) — nooit op tussenwaarden.
 */
export function roundTo1Decimal(value) {
    return Math.round(value * 10) / 10;
}
/**
 * Combineert `resolvePeriodsAndWeights` en `calculateCoreTaskBlock` tot het
 * finale gewogen takenblok (AD-6 formule):
 *   50 x Σ_tijdvak(gewicht x Σ_kerntaak(pct/100 x score/5))
 *
 * Dit is de functie die zowel `evaluationService.calculateScores`
 * (backend, autoritatief) als de wizard-live-weergave (frontend) aanroept
 * — niemand herimplementeert deze samenstelling.
 *
 * Bij 0 gedekte maanden (`totalCoveredMonths === 0`): `totalPoints: 0`,
 * lege `periodBreakdown` — de aanroepende code (finalize, Story 4.4)
 * vertaalt dit naar de `NO_PERIOD_COVERAGE`-blokkade.
 */
export function calculateWeightedCoreTaskBlock(periods, scoresPerPeriod, evaluationWindow) {
    const { weights, totalCoveredMonths } = resolvePeriodsAndWeights(evaluationWindow, periods);
    if (totalCoveredMonths === 0) {
        return { totalPoints: 0, totalCoveredMonths: 0, periodBreakdown: [] };
    }
    const periodBreakdown = weights.map((w) => {
        const assignments = scoresPerPeriod[w.periodId] ?? [];
        const { totalPoints: blockPoints } = calculateCoreTaskBlock(assignments);
        return {
            periodId: w.periodId,
            weight: w.weight,
            months: w.months,
            blockPoints,
        };
    });
    const totalPoints = periodBreakdown.reduce((sum, p) => sum + p.weight * p.blockPoints, 0);
    return { totalPoints, totalCoveredMonths, periodBreakdown };
}
//# sourceMappingURL=coreTaskScoring.js.map